import type { MissionEvent } from "./api";

/**
 * A mission's route, built from the only positions the app records: one per
 * button press. There is no GPS track between them, so each stretch is a
 * straight line and its speed is an average — right for a direct transit,
 * low for a stretch spent orbiting a fire before the drop.
 */

export type RoutePoint = {
  id: string;
  event: string;
  recordedAt: string;
  lng: number;
  lat: number;
  suburb: string | null;
};

export type Stretch = {
  from: RoutePoint;
  to: RoutePoint;
  metres: number;
  seconds: number;
  /** Null when too little time passed to call it a speed. */
  knots: number | null;
};

export type Route = {
  points: RoutePoint[];
  stretches: Stretch[];
  /** Slowest and fastest stretch, for the colour scale and its legend. */
  range: { min: number; max: number } | null;
};

/**
 * Only an identical fix is the same place. Anything further apart gets a line:
 * ten metres in ten minutes is worth seeing — that is an aircraft hovering.
 */
const SAME_PLACE_METRES = 1;

/** Under this, GPS wobble divided by a second or two reads as a jet. */
const MIN_STRETCH_SECONDS = 10;

const METRES_PER_SECOND_TO_KNOTS = 1.943844;

export function metresBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6_371_000;
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function buildRoute(events: MissionEvent[]): Route {
  const points: RoutePoint[] = events
    .filter((e) => e.latitude !== null && e.longitude !== null)
    .sort((a, b) => a.sequence - b.sequence)
    .map((e) => ({
      id: e.id,
      event: e.event,
      recordedAt: e.recordedAt,
      lng: e.longitude!,
      lat: e.latitude!,
      suburb: e.suburb,
    }));

  const stretches: Stretch[] = [];
  let departed: RoutePoint | undefined;
  for (const p of points) {
    if (!departed) {
      departed = p;
      continue;
    }
    const metres = metresBetween(departed, p);
    if (metres < SAME_PLACE_METRES) {
      // Still here. A stretch leaves from the last thing done at a place — the
      // takeoff, not the engine start before it — or time on the pad counts
      // as flying slowly.
      departed = p;
      continue;
    }
    const seconds = (Date.parse(p.recordedAt) - Date.parse(departed.recordedAt)) / 1000;
    stretches.push({
      from: departed,
      to: p,
      metres,
      seconds,
      knots: seconds >= MIN_STRETCH_SECONDS ? (metres / seconds) * METRES_PER_SECOND_TO_KNOTS : null,
    });
    departed = p;
  }

  const speeds = stretches.map((s) => s.knots).filter((k): k is number => k !== null);
  return {
    points,
    stretches,
    range: speeds.length ? { min: Math.min(...speeds), max: Math.max(...speeds) } : null,
  };
}

/**
 * Slow to fast, in iOS system blues and purples — kept clear of the orange,
 * yellow and red the event dots use, so a line is never mistaken for an event.
 */
export const SPEED_RAMP = ["#64d2ff", "#0a84ff", "#5e5ce6", "#bf5af2"] as const;
export const NO_SPEED_COLOUR = "#8e8e93";

function hex(c: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16)) as [number, number, number];
}

/** Colour for a speed within the route's own range. */
export function speedColour(knots: number | null, range: Route["range"]): string {
  if (knots === null || !range) return NO_SPEED_COLOUR;
  const span = range.max - range.min;
  // One stretch, or all alike: the middle of the scale, not its slow end.
  const t = span < 1 ? 0.5 : (knots - range.min) / span;
  const scaled = Math.min(Math.max(t, 0), 1) * (SPEED_RAMP.length - 1);
  const i = Math.min(Math.floor(scaled), SPEED_RAMP.length - 2);
  const f = scaled - i;
  const [a, b] = [hex(SPEED_RAMP[i]!), hex(SPEED_RAMP[i + 1]!)];
  return `rgb(${a.map((v, k) => Math.round(v + (b[k]! - v) * f)).join(",")})`;
}

/**
 * The route as one continuous line with colour stops along it, the way running
 * apps shade a run: each turn takes the average of the stretches either side,
 * so colour blends through a turn instead of stepping at it.
 */
export function routeLine(route: Route): { coordinates: [number, number][]; gradient: unknown[] } | null {
  const { stretches, range } = route;
  if (stretches.length === 0) return null;

  const vertices = [stretches[0]!.from, ...stretches.map((s) => s.to)];
  const total = stretches.reduce((sum, s) => sum + s.metres, 0);

  const stops: unknown[] = [];
  let along = 0;
  vertices.forEach((_, i) => {
    const either = [stretches[i - 1]?.knots, stretches[i]?.knots].filter((k): k is number => typeof k === "number");
    const knots = either.length ? either.reduce((a, b) => a + b, 0) / either.length : null;
    stops.push(along / total, speedColour(knots, range));
    if (i < stretches.length) along += stretches[i]!.metres;
  });
  // Line progress must strictly increase; the last stop can land a hair under 1.
  stops[stops.length - 2] = 1;

  return {
    coordinates: vertices.map((v) => [v.lng, v.lat]),
    gradient: ["interpolate", ["linear"], ["line-progress"], ...stops],
  };
}
